/**
 * Geodesy primitives — rhumb-line distance and bearing on the WGS84 sphere.
 *
 * For chartwork on training cells (small areas, low latitudes), rhumb lines are the
 * right thing: that's what students plot on paper. Great-circle work comes if and
 * when we add ocean-passage planning.
 *
 * Why a hand-rolled module rather than Turf alone? The teaching side has to be
 * exactly right. We need to be able to point at the function that produced a value
 * and explain the steps, and we need it covered by tests we own. Turf is fine for
 * convenience geometry around it.
 *
 * Conventions:
 *   - Positions are [lon, lat] in WGS84 degrees, matching GeoJSON.
 *   - Bearings are degrees true, 0 = north, increasing clockwise.
 *   - Distances are nautical miles (1 nm = 1 minute of latitude = 1852 m).
 */

/** Equatorial radius used for nm conversions. 1 minute of latitude = 1 nm by definition. */
export const NM_PER_DEGREE_LAT = 60;
export const METRES_PER_NM = 1852;
/** Radius of the Earth in nm, derived so 1° of latitude = 60 nm exactly. */
export const EARTH_RADIUS_NM = (180 * NM_PER_DEGREE_LAT) / Math.PI;

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

/** Wrap a bearing into the range [0, 360). */
export function normaliseBearing(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** Wrap a longitude into [-180, 180]. */
export function normaliseLon(deg: number): number {
  let x = ((deg + 180) % 360) - 180;
  if (x < -180) x += 360;
  return x;
}

/**
 * Rhumb-line distance in nautical miles between two [lon, lat] positions.
 *
 * Uses the Mercator-stretched latitude form so the bearing along the line is
 * constant, which is what students draw on a paper chart.
 */
export function rhumbDistanceNm(a: [number, number], b: [number, number]): number {
  const [lon1, lat1] = a;
  const [lon2, lat2] = b;
  const phi1 = lat1 * D2R;
  const phi2 = lat2 * D2R;
  const dPhi = phi2 - phi1;
  let dLon = (lon2 - lon1) * D2R;
  // Stretch the latitude (Mercator-style) so the rhumb line is straight.
  const dPsi = Math.log(Math.tan(Math.PI / 4 + phi2 / 2) / Math.tan(Math.PI / 4 + phi1 / 2));
  const q = Math.abs(dPsi) > 1e-12 ? dPhi / dPsi : Math.cos(phi1);
  // Take the shorter way around the meridian if we crossed ±180°.
  if (Math.abs(dLon) > Math.PI) dLon = dLon > 0 ? dLon - 2 * Math.PI : dLon + 2 * Math.PI;
  const dist = Math.sqrt(dPhi * dPhi + q * q * dLon * dLon) * EARTH_RADIUS_NM;
  return dist;
}

/**
 * Rhumb-line bearing (initial / constant) in degrees true, from a to b.
 *
 * Returns 0..360. North = 0, east = 90.
 */
export function rhumbBearingDeg(a: [number, number], b: [number, number]): number {
  const [lon1, lat1] = a;
  const [lon2, lat2] = b;
  const phi1 = lat1 * D2R;
  const phi2 = lat2 * D2R;
  let dLon = (lon2 - lon1) * D2R;
  if (Math.abs(dLon) > Math.PI) dLon = dLon > 0 ? dLon - 2 * Math.PI : dLon + 2 * Math.PI;
  const dPsi = Math.log(Math.tan(Math.PI / 4 + phi2 / 2) / Math.tan(Math.PI / 4 + phi1 / 2));
  const theta = Math.atan2(dLon, dPsi);
  return normaliseBearing(theta * R2D);
}

/**
 * Walk a rhumb line: start at `from`, head along `bearing` (deg true) for `distanceNm`,
 * return the new [lon, lat].
 */
export function rhumbDestination(
  from: [number, number],
  bearingDeg: number,
  distanceNm: number,
): [number, number] {
  const [lon1, lat1] = from;
  const phi1 = lat1 * D2R;
  const bearing = bearingDeg * D2R;
  const delta = distanceNm / EARTH_RADIUS_NM; // angular distance in radians
  const phi2 = phi1 + delta * Math.cos(bearing);
  // Avoid a divide-by-zero when heading due east/west — q -> cos(phi).
  const dPhi = phi2 - phi1;
  const dPsi = Math.log(Math.tan(Math.PI / 4 + phi2 / 2) / Math.tan(Math.PI / 4 + phi1 / 2));
  const q = Math.abs(dPsi) > 1e-12 ? dPhi / dPsi : Math.cos(phi1);
  const dLon = (delta * Math.sin(bearing)) / q;
  const lon2 = normaliseLon(lon1 + dLon * R2D);
  return [lon2, phi2 * R2D];
}

/**
 * A true-distance circle: the ring of points exactly `radiusNm` nautical miles
 * from `centre`, sampled evenly around 360° by walking a rhumb line out on each
 * bearing. Because it's built from ground distance (not a screen-pixel radius),
 * it stays correct at any latitude — at 56°N a naive equal-pixel circle on the
 * Mercator map would understate the real distance east-west, but this doesn't.
 * Returns a closed ring ([0] === [steps]) for use as a LineString/Polygon.
 *
 * Shared by the range rings, the VRM, and the measure tool so their circles are
 * built the one way and a ring's stated distance always matches the ruler.
 */
export function rhumbCircle(
  centre: [number, number],
  radiusNm: number,
  steps = 72,
): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i <= steps; i++) out.push(rhumbDestination(centre, (i / steps) * 360, radiusNm));
  return out;
}
